'use client'

/**
 * Client do bloco de Listagem Comparativa (genérico — ex.: comparar seguradoras).
 *
 * DIRIGIDO POR CONFIG. Recebe itens/colunas/
 * rótulos/opções já resolvidos pelo Component (server) via `ComparisonListingClientProps`.
 * Não conhece nenhum domínio específico — render por `column.kind` + atributos por `key`.
 *
 * SEO (plano §7):
 *  - SEO-01: painel de detalhes SEMPRE no DOM (via `DetailsReveal`), colapsado por CSS.
 *  - SEO-03: nome do operador na tabela desktop é `<h3 className="sr-only">` (consistente c/ mobile).
 *  - SEO-04: CTA aplica `options.ctaRel` (+ `target` quando `ctaOpenNewTab`).
 *  - SEO-06: `colSpan` da linha do painel = nº de colunas visíveis (`columns.length`).
 *  - `<caption className="sr-only">`; `<th scope="col">` no header e `<th scope="row">` no nome.
 */

import { ArrowUpDown, Globe } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utilities/ui'

import {
  attributeCell,
  buildSortOptions,
  filterByRegion,
  regionFlagSrc,
  sortItems,
  type ComparisonListingClientProps,
  type ListingItem,
  type ResolvedColumn,
  type SortOption,
} from '@/blocks/ComparisonListing/lib'

import {
  DetailsReveal,
  ExpandedPanel,
  InfoTip,
  LogoLink,
  listingTableStyles,
  MobileMetrics,
  OfferActions,
  RankBadge,
  StarScore,
  TableMetricCell,
  VerifiedBadge,
  type ListingMobileMetric,
} from './listing-primitives'

const ALL_REGIONS = 'all'

function detailsIdFor(itemId: string): string {
  return `comparison-listing-details-${itemId}`
}

/** Aplica o template `{n} {entity} encontrados` (substitui {n} e {entity}). */
function renderResultCount(template: string, n: number, entity: string): string {
  return template.replace(/\{n\}/g, String(n)).replace(/\{entity\}/g, entity)
}

// ── Link «Ver revisão» ───────────────────────────────────────────────────────
function ReviewLink({ href, label, text }: { href: string; label: string; text: string }) {
  return (
    <a
      href={href}
      aria-label={label}
      className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:text-primary/80 hover:decoration-primary sm:text-xs"
    >
      {text}
    </a>
  )
}

// ── Pilha de bandeiras (regiões) ─────────────────────────────────────────────
/** Máx. de bandeiras renderizadas por item; o excedente colapsa num chip "+N" (protege a coluna). */
const MAX_FLAGS_SHOWN = 5

function RegionFlagsStack({
  codes,
  names,
  size = 'md',
}: {
  codes: string[]
  names: Record<string, string>
  size?: 'sm' | 'md'
}) {
  const sizeClass = size === 'sm' ? 'size-6' : 'size-7'
  const overlap = size === 'sm' ? '-ml-2' : '-ml-2.5'
  const nameOf = (code: string) => names[code] ?? code.toUpperCase()

  if (!codes.length) {
    return <span className="text-muted-foreground text-xs">—</span>
  }

  const shown = codes.slice(0, MAX_FLAGS_SHOWN)
  const extra = codes.length - shown.length

  return (
    <div className="flex items-center" role="img" aria-label={codes.map(nameOf).join(', ')}>
      {shown.map((code, index) => (
        <Tooltip key={code}>
          <TooltipTrigger asChild>
            {/* Bandeira circular via <img> flagcdn (contrato §6); NÃO next/image. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={regionFlagSrc(code, size === 'sm' ? 'w40' : 'w80')}
              alt=""
              aria-hidden
              width={size === 'sm' ? 24 : 28}
              height={size === 'sm' ? 24 : 28}
              loading="lazy"
              decoding="async"
              className={cn(
                sizeClass,
                'rounded-full border-2 border-background object-cover ring-1 ring-border/50',
                index > 0 && overlap,
              )}
              style={{ zIndex: codes.length - index }}
            />
          </TooltipTrigger>
          <TooltipContent>{nameOf(code)}</TooltipContent>
        </Tooltip>
      ))}
      {extra > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                sizeClass,
                overlap,
                'z-0 inline-flex items-center justify-center rounded-full border-2 border-background bg-muted text-[10px] font-semibold text-muted-foreground ring-1 ring-border/50',
              )}
            >
              +{extra}
            </span>
          </TooltipTrigger>
          <TooltipContent>{codes.slice(MAX_FLAGS_SHOWN).map(nameOf).join(', ')}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

// ── Ícone do filtro de região (no <SelectTrigger>) ───────────────────────────
function RegionFilterIcon({ regionId, className }: { regionId: string; className?: string }) {
  if (regionId === ALL_REGIONS) {
    return (
      <span
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground',
          className,
        )}
        aria-hidden
      >
        <Globe className="size-3" strokeWidth={2} />
      </span>
    )
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={regionFlagSrc(regionId)}
      alt=""
      width={20}
      height={20}
      loading="lazy"
      decoding="async"
      className={cn(
        'size-5 shrink-0 rounded-full border border-border/60 object-cover ring-1 ring-border/40',
        className,
      )}
      aria-hidden
    />
  )
}

export function ComparisonListingClient({
  items,
  columns,
  labels,
  options,
  regionLabelMap,
}: ComparisonListingClientProps) {
  const [regionId, setRegionId] = useState<string>(ALL_REGIONS)
  const [sortId, setSortId] = useState<string>('default')
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})

  const toggleRow = (id: string) => setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }))

  // Nome de uma região no locale resolvido (server) — fallback: código em maiúsculo.
  const nameOf = (code: string) => regionLabelMap[code] ?? code.toUpperCase()

  const sortOptions = useMemo<SortOption[]>(
    () => buildSortOptions(columns, labels.sortDefaultLabel),
    [columns, labels.sortDefaultLabel],
  )

  const activeSort = useMemo(
    () => sortOptions.find((o) => o.id === sortId),
    [sortOptions, sortId],
  )

  // Regiões disponíveis nos itens (para o filtro do visitante) — só as presentes nos dados, ordem
  // alfabética pelo rótulo no locale ativo (suporta o catálogo completo sem poluir o dropdown).
  const regionCodes = useMemo(() => {
    const set = new Set<string>()
    for (const it of items) for (const r of it.regions) set.add(r)
    return Array.from(set).sort((a, b) =>
      (regionLabelMap[a] ?? a).localeCompare(regionLabelMap[b] ?? b, undefined, { sensitivity: 'base' }),
    )
  }, [items, regionLabelMap])

  const visibleItems = useMemo(
    () => sortItems(filterByRegion(items, regionId), activeSort),
    [items, regionId, activeSort],
  )

  const resultCount = renderResultCount(
    labels.resultCountTemplate,
    visibleItems.length,
    labels.entityPlural,
  )

  const activeRegionLabel = regionId === ALL_REGIONS ? null : nameOf(regionId)

  return (
    <TooltipProvider delayDuration={200}>
      <section
        id={options.anchorId ?? undefined}
        className="flex flex-col gap-4 sm:gap-6"
      >
        {/* ── Barra de filtros ── */}
        {options.enableRegionFilter || options.enableSort ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid w-full grid-cols-2 gap-2 sm:max-w-md sm:gap-3">
              {options.enableRegionFilter ? (
                <div className="min-w-0">
                  <Label
                    htmlFor="comparison-listing-region"
                    className="mb-1 block text-[11px] font-semibold sm:mb-1.5 sm:text-sm"
                  >
                    {labels.regionFilterLabel}
                  </Label>
                  <Select value={regionId} onValueChange={setRegionId}>
                    <SelectTrigger
                      id="comparison-listing-region"
                      className="h-9 w-full min-w-0 gap-2 px-2.5 text-xs sm:text-sm [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate"
                      aria-label={labels.regionFilterLabel}
                    >
                      {/* Sem ícone explícito aqui: o SelectValue já renderiza a bandeira da opção
                          ativa (evita ícone duplicado). */}
                      <SelectValue placeholder={labels.allRegionsLabel} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_REGIONS}>
                        <span className="inline-flex items-center gap-2">
                          <RegionFilterIcon regionId={ALL_REGIONS} />
                          {labels.allRegionsLabel}
                        </span>
                      </SelectItem>
                      {regionCodes.map((code) => (
                        <SelectItem key={code} value={code}>
                          <span className="inline-flex items-center gap-2">
                            <RegionFilterIcon regionId={code} />
                            {nameOf(code)}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {options.enableSort ? (
                <div className="min-w-0">
                  <Label
                    htmlFor="comparison-listing-sort"
                    className="mb-1 block text-[11px] font-semibold sm:mb-1.5 sm:text-sm"
                  >
                    {labels.sortLabel}
                  </Label>
                  <Select value={sortId} onValueChange={setSortId}>
                    <SelectTrigger
                      id="comparison-listing-sort"
                      className="h-9 w-full min-w-0 gap-2 px-2.5 text-xs sm:text-sm [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate"
                      aria-label={labels.sortLabel}
                    >
                      {/* <div> (não <span>) p/ escapar do `[&>span]:line-clamp-1` do SelectTrigger,
                          que sobrepunha o `inline-flex` e descentralizava o ícone. */}
                      <div
                        className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                        aria-hidden
                      >
                        <ArrowUpDown className="size-3" strokeWidth={2} />
                      </div>
                      <SelectValue placeholder={labels.sortDefaultLabel} />
                    </SelectTrigger>
                    <SelectContent>
                      {sortOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ── Contador de resultados ── */}
        <p className="text-muted-foreground text-xs sm:text-sm">
          {resultCount}
          {activeRegionLabel ? (
            <>
              {' '}
              ·{' '}
              <span className="inline-flex items-center gap-1.5">
                <RegionFilterIcon regionId={regionId} className="size-4" />
                {activeRegionLabel}
              </span>
            </>
          ) : null}
        </p>

        {/* ── Listagem ── */}
        {visibleItems.length === 0 ? (
          <p className="text-muted-foreground rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm">
            {labels.emptyStateText}
          </p>
        ) : (
          <>
            {/* MOBILE: cards */}
            <div className="flex flex-col gap-2.5 sm:gap-3 lg:hidden">
              {visibleItems.map((item, index) => (
                <MobileCard
                  key={item.id}
                  item={item}
                  rank={index + 1}
                  columns={columns}
                  labels={labels}
                  options={options}
                  regionLabelMap={regionLabelMap}
                  open={Boolean(openRows[item.id])}
                  onToggle={() => toggleRow(item.id)}
                />
              ))}
            </div>

            {/* DESKTOP: tabela */}
            <div className="hidden lg:block">
              <div className="overflow-x-auto overscroll-x-contain pb-2 pr-1 sm:pr-2">
                <table
                  className="w-full table-fixed border-collapse text-xs"
                  style={{ minWidth: `${options.tableMinWidthRem}rem` }}
                >
                  <caption className="sr-only">
                    {`Tabela comparativa de ${labels.entityPlural}`}
                  </caption>
                  <thead>
                    <tr>
                      {columns.map((col, index) => (
                        <th
                          key={col.id}
                          scope="col"
                          className={cn(
                            listingTableStyles.headerCell,
                            col.widthClass,
                            col.align === 'center' && 'text-center',
                            col.align === 'right' && 'text-right',
                            index === 0 && 'rounded-l pl-4',
                            index === columns.length - 1 && 'rounded-r px-4',
                          )}
                          aria-label={col.kind === 'actions' && !col.label ? 'Ações' : undefined}
                        >
                          {col.label ? (
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              <InfoTip
                                label={col.tooltip ?? col.label}
                                text={col.tooltip}
                                variant="onHeader"
                              />
                            </span>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item, index) => (
                      <TableRow
                        key={item.id}
                        item={item}
                        rank={index + 1}
                        columns={columns}
                        labels={labels}
                        options={options}
                        regionLabelMap={regionLabelMap}
                        open={Boolean(openRows[item.id])}
                        onToggle={() => toggleRow(item.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>
    </TooltipProvider>
  )
}

// ── Helpers de render por kind ───────────────────────────────────────────────

function itemCtaLabel(item: ListingItem, labels: ListingLabelsLike): string {
  return item.ctaLabel?.trim() || labels.ctaLabel
}

type ListingLabelsLike = ComparisonListingClientProps['labels']
type ListingOptionsLike = ComparisonListingClientProps['options']

function verifiedProps(labels: ListingLabelsLike) {
  return {
    ariaLabel: labels.verifiedTooltip,
    label: labels.verifiedLabel,
    tooltip: labels.verifiedTooltip,
  }
}

/** Métricas do card mobile = colunas de atributo (na ordem do config). */
function mobileMetricsFor(
  item: ListingItem,
  columns: ResolvedColumn[],
): ListingMobileMetric[] {
  return columns
    .filter((c) => c.kind === 'attribute' && c.attributeKey)
    .map((c) => {
      const { value, note } = attributeCell(item, c.attributeKey as string)
      return {
        label: c.label,
        tooltip: c.tooltip,
        value,
        note,
      }
    })
}

// ── DESKTOP: célula por kind ─────────────────────────────────────────────────
function TableKindCell({
  item,
  rank,
  col,
  labels,
  options,
  regionLabelMap,
  open,
  onToggle,
}: {
  item: ListingItem
  rank: number
  col: ResolvedColumn
  labels: ListingLabelsLike
  options: ListingOptionsLike
  regionLabelMap: Record<string, string>
  open: boolean
  onToggle: () => void
}) {
  const detailsId = detailsIdFor(item.id)
  const scoreMax = item.scoreMax || options.scoreMax

  switch (col.kind) {
    case 'name':
      return (
        <th
          scope="row"
          className={cn(listingTableStyles.bodyCell, 'px-4 text-left font-normal', col.widthClass)}
        >
          <div className={cn(listingTableStyles.bodyCellContent, 'items-start text-left')}>
            {options.showRanking ? (
              <RankBadge rank={options.highlightTopRank ? rank : Math.max(rank, 2)} />
            ) : null}
            {/* SEO-03: nome como heading sr-only (consistente com o mobile). */}
            <h3 className="sr-only">{item.name}</h3>
            <LogoLink
              href={item.reviewHref}
              label={item.reviewLabel ?? item.name}
              name={item.name}
              src={item.logoSrc}
              alt={item.logoAlt}
              width={item.logoWidth}
              height={item.logoHeight}
              logoBackground={item.logoBackground}
            />
            {options.showVerifiedBadge && item.verified ? (
              <VerifiedBadge className="whitespace-nowrap" {...verifiedProps(labels)} />
            ) : null}
          </div>
        </th>
      )

    case 'region':
      return (
        <td className={cn(listingTableStyles.bodyCell, col.widthClass, 'px-2 text-center')}>
          <div className={cn(listingTableStyles.bodyCellContent, 'items-center justify-center')}>
            <RegionFlagsStack codes={item.regions} names={regionLabelMap} />
          </div>
        </td>
      )

    case 'attribute': {
      const { value, note } = attributeCell(item, col.attributeKey ?? '')
      return <TableMetricCell value={value} note={note} align={col.align} />
    }

    case 'score':
      return (
        <td className={cn(listingTableStyles.bodyCell, col.widthClass)}>
          <div className={cn(listingTableStyles.bodyCellContent, 'items-start gap-1.5')}>
            {item.score !== null ? <StarScore score={item.score} max={scoreMax} /> : null}
            {item.reviewHref ? (
              <ReviewLink
                href={item.reviewHref}
                label={item.reviewLabel ?? labels.reviewLinkLabel}
                text={labels.reviewLinkLabel}
              />
            ) : null}
          </div>
        </td>
      )

    case 'actions':
      return (
        <td className={cn(listingTableStyles.bodyCell, col.widthClass, 'px-4 align-top')}>
          <div className="mx-auto flex w-full max-w-[12rem] justify-center">
            <OfferActions
              ctaHref={item.ctaHref}
              ctaLabel={itemCtaLabel(item, labels)}
              ctaRel={options.ctaRel}
              ctaOpenNewTab={options.ctaOpenNewTab}
              detailsId={detailsId}
              open={open}
              onToggle={onToggle}
              showMoreLabel={labels.showMoreLabel}
              showLessLabel={labels.showLessLabel}
              enableDetails={options.enableDetailsPanel}
              compact
              containerClassName="max-w-full"
            />
          </div>
        </td>
      )

    default:
      return <td className={cn(listingTableStyles.bodyCell, col.widthClass)} />
  }
}

function TableRow({
  item,
  rank,
  columns,
  labels,
  options,
  regionLabelMap,
  open,
  onToggle,
}: {
  item: ListingItem
  rank: number
  columns: ResolvedColumn[]
  labels: ListingLabelsLike
  options: ListingOptionsLike
  regionLabelMap: Record<string, string>
  open: boolean
  onToggle: () => void
}) {
  const detailsId = detailsIdFor(item.id)

  return (
    <>
      <tr className="border-b border-border">
        {columns.map((col) => (
          <TableKindCell
            key={col.id}
            item={item}
            rank={rank}
            col={col}
            labels={labels}
            options={options}
            regionLabelMap={regionLabelMap}
            open={open}
            onToggle={onToggle}
          />
        ))}
      </tr>
      {/* SEO-06: colSpan dinâmico = nº de colunas visíveis. SEO-01: painel sempre no DOM. */}
      {options.enableDetailsPanel ? (
        <tr className="border-b border-border">
          <td colSpan={columns.length} className="p-0">
            <DetailsReveal open={open} detailsId={detailsId} contentKey={item.id}>
              <ExpandedPanel
                itemId={item.id}
                heading={labels.expertTakeHeading}
                expertTake={item.expertTake}
                prosHeading={labels.prosHeading}
                pros={item.pros}
                consHeading={labels.consHeading}
                cons={item.cons}
                panelLabel={`${labels.expertTakeHeading} — ${item.name}`}
              />
            </DetailsReveal>
          </td>
        </tr>
      ) : null}
    </>
  )
}

// ── MOBILE: card ─────────────────────────────────────────────────────────────
function MobileCard({
  item,
  rank,
  columns,
  labels,
  options,
  regionLabelMap,
  open,
  onToggle,
}: {
  item: ListingItem
  rank: number
  columns: ResolvedColumn[]
  labels: ListingLabelsLike
  options: ListingOptionsLike
  regionLabelMap: Record<string, string>
  open: boolean
  onToggle: () => void
}) {
  const detailsId = detailsIdFor(item.id)
  const scoreMax = item.scoreMax || options.scoreMax
  const metrics = mobileMetricsFor(item, columns)

  return (
    <article
      aria-labelledby={`comparison-${item.id}-title`}
      className="overflow-hidden rounded-md border border-border"
    >
      <header className="flex items-start justify-between gap-2 bg-primary px-2.5 py-1.5 text-primary-foreground">
        <div className="flex min-w-0 items-center gap-1.5">
          {options.showRanking ? <RankBadge rank={rank} compact /> : null}
          <h3
            id={`comparison-${item.id}-title`}
            className="line-clamp-2 min-w-0 text-left text-[11px] font-semibold leading-tight"
          >
            {item.name}
          </h3>
        </div>
      </header>

      <div className="flex items-center justify-between gap-2 px-2.5 py-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <LogoLink
            href={item.reviewHref}
            label={item.reviewLabel ?? item.name}
            name={item.name}
            src={item.logoSrc}
            alt={item.logoAlt}
            width={item.logoWidth}
            height={item.logoHeight}
            logoBackground={item.logoBackground}
            compact
          />
          {item.regions.length ? (
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground text-[10px] font-medium">
                {labels.regionFilterLabel}
              </span>
              <RegionFlagsStack codes={item.regions} size="sm" names={regionLabelMap} />
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-0.5">
          {options.showVerifiedBadge && item.verified ? (
            <VerifiedBadge compact {...verifiedProps(labels)} />
          ) : null}
          {item.score !== null ? <StarScore score={item.score} max={scoreMax} compact /> : null}
          {item.reviewHref ? (
            <ReviewLink
              href={item.reviewHref}
              label={item.reviewLabel ?? labels.reviewLinkLabel}
              text={labels.reviewLinkLabel}
            />
          ) : null}
        </div>
      </div>

      {metrics.length ? <MobileMetrics metrics={metrics} /> : null}

      <footer className="flex justify-center border-t border-border/60 px-2.5 py-2.5">
        <OfferActions
          ctaHref={item.ctaHref}
          ctaLabel={itemCtaLabel(item, labels)}
          ctaRel={options.ctaRel}
          ctaOpenNewTab={options.ctaOpenNewTab}
          detailsId={detailsId}
          open={open}
          onToggle={onToggle}
          showMoreLabel={labels.showMoreLabel}
          showLessLabel={labels.showLessLabel}
          enableDetails={options.enableDetailsPanel}
          compact
        />
      </footer>

      {/* SEO-01: painel sempre no DOM, colapsado por CSS. */}
      {options.enableDetailsPanel ? (
        <DetailsReveal open={open} detailsId={detailsId} contentKey={item.id}>
          <ExpandedPanel
            itemId={item.id}
            heading={labels.expertTakeHeading}
            expertTake={item.expertTake}
            prosHeading={labels.prosHeading}
            pros={item.pros}
            consHeading={labels.consHeading}
            cons={item.cons}
            panelLabel={`${labels.expertTakeHeading} — ${item.name}`}
          />
        </DetailsReveal>
      ) : null}
    </article>
  )
}
